import { convidarCondutorSchema, emailSchema, nomeSchema, telefoneSchema } from '@locaveri/shared'
import { LoaderCircle, UserPlus } from 'lucide-react'
import { type ReactElement, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { Campo } from '@/components/form/campo'
import { EmptyState } from '@/components/state/empty-state'
import { ErrorState } from '@/components/state/error-state'
import { LoadingState } from '@/components/state/loading-state'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import { BadgeDeStatusDoUsuario } from '@/features/usuarios/badges'
import {
  aplicarErrosDaApi,
  aplicarErrosDoZod,
  validarCom,
  validarComSeInformado,
} from '@/lib/formulario'
import { apenasDigitos, mascararTelefoneProgressivo } from '@/lib/mascaras'

import { type Conta, nomeDaConta, useCondutores, useConvidarCondutor } from './api'

/**
 * Condutores autorizados de uma conta.
 *
 * ---------------------------------------------------------------------------
 * É AQUI QUE O CLIENTE CORPORATIVO DEIXA DE SER UM CADASTRO COMUM
 * ---------------------------------------------------------------------------
 * A empresa é a titular: ela paga, assina e recebe a fatura. Quem dirige são N
 * pessoas, cada uma com acesso próprio ao app, e é o vínculo delas com a conta
 * que permite indicar condutor em multa, rastrear quem retirou o veículo e
 * ratear por centro de custo. Sem esta tela, o jeito de dar acesso a dez
 * motoristas seria dez cadastros de cobrança — que é exatamente o que o Gap #9
 * do ROADMAP existe para evitar.
 *
 * O condutor não recebe senha de ninguém: ele recebe convite e define a dele,
 * como qualquer usuário.
 */

const CAMPOS = ['nome', 'email', 'telefone', 'centroDeCusto'] as const

interface FormularioDeCondutor {
  nome: string
  email: string
  telefone: string
  centroDeCusto: string
}

export interface DialogoDeCondutoresProps {
  readonly conta: Conta | null
  readonly aoFechar: () => void
  /** `false` esconde o formulário e deixa a lista só para leitura. */
  readonly podeConvidar: boolean
}

export function DialogoDeCondutores({
  conta,
  aoFechar,
  podeConvidar,
}: DialogoDeCondutoresProps): ReactElement {
  const [erroGeral, setErroGeral] = useState<string | null>(null)

  const consulta = useCondutores(conta?.id ?? null)
  const convidar = useConvidarCondutor()

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    reset,
    watch,
    formState: { errors, isSubmitted },
  } = useForm<FormularioDeCondutor>({
    defaultValues: { nome: '', email: '', telefone: '', centroDeCusto: '' },
  })

  const telefone = watch('telefone')

  const aoEnviar = handleSubmit((valores) => {
    setErroGeral(null)

    if (conta === null) {
      return
    }

    const telefoneLimpo = apenasDigitos(valores.telefone)
    const entrada = {
      nome: valores.nome,
      email: valores.email,
      ...(telefoneLimpo.length > 0 ? { telefone: telefoneLimpo } : {}),
      ...(valores.centroDeCusto.trim().length > 0
        ? { centroDeCusto: valores.centroDeCusto.trim() }
        : {}),
    }

    const validado = convidarCondutorSchema.safeParse(entrada)

    if (!validado.success) {
      setErroGeral(aplicarErrosDoZod(validado.error, setError, CAMPOS))
      return
    }

    convidar.mutate(
      { contaId: conta.id, dados: validado.data },
      {
        onSuccess: (condutor) => {
          toast.success('Convite enviado', {
            description: `${condutor.nome} vai definir a própria senha pelo link.`,
          })
          reset()
        },
        onError: (causa: unknown) => {
          setErroGeral(aplicarErrosDaApi(causa, setError, CAMPOS))
        },
      },
    )
  })

  const condutores = consulta.data?.itens ?? []

  return (
    <Dialog
      open={conta !== null}
      onOpenChange={(proximo) => {
        if (!proximo) {
          aoFechar()
        }
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Condutores autorizados</DialogTitle>
          <DialogDescription>
            {conta === null
              ? 'Selecione uma conta.'
              : `Quem pode retirar veículo pela conta ${nomeDaConta(conta)}.`}
          </DialogDescription>
        </DialogHeader>

        {consulta.isPending && conta !== null ? (
          <LoadingState rotulo="Carregando condutores…" linhas={2} />
        ) : consulta.isError ? (
          <ErrorState
            titulo="Não foi possível carregar os condutores"
            erro={consulta.error}
            aoTentarNovamente={() => {
              void consulta.refetch()
            }}
          />
        ) : condutores.length === 0 ? (
          <EmptyState
            titulo="Nenhum condutor vinculado"
            descricao="Convide as pessoas autorizadas a dirigir pela conta. Cada uma recebe o próprio acesso."
          />
        ) : (
          <ul className="divide-y divide-border rounded-lg border border-border bg-surface">
            {condutores.map((condutor) => (
              <li key={condutor.id} className="flex flex-wrap items-center gap-2 px-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">{condutor.nome}</p>
                  <p className="truncate text-xs text-fg-secondary">
                    {condutor.email}
                    {condutor.centroDeCusto === null ? '' : ` · ${condutor.centroDeCusto}`}
                  </p>
                </div>
                <BadgeDeStatusDoUsuario status={condutor.status} />
              </li>
            ))}
          </ul>
        )}

        {podeConvidar && conta !== null ? (
          <>
            <Separator />

            <form
              noValidate
              onSubmit={(evento) => {
                void aoEnviar(evento)
              }}
              className="space-y-4"
            >
              <p className="text-sm font-medium text-foreground">Convidar condutor</p>

              {erroGeral === null ? null : (
                <Alert variant="danger">
                  <AlertDescription>{erroGeral}</AlertDescription>
                </Alert>
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                <Campo rotulo="Nome" obrigatorio erro={errors.nome?.message}>
                  {(campo) => (
                    <Input
                      {...campo}
                      {...register('nome', {
                        required: 'informe o nome',
                        validate: validarCom(nomeSchema),
                      })}
                      disabled={convidar.isPending}
                    />
                  )}
                </Campo>

                <Campo rotulo="E-mail" obrigatorio erro={errors.email?.message}>
                  {(campo) => (
                    <Input
                      {...campo}
                      {...register('email', {
                        required: 'informe o e-mail',
                        validate: validarCom(emailSchema),
                      })}
                      type="email"
                      disabled={convidar.isPending}
                    />
                  )}
                </Campo>

                <Campo rotulo="Telefone" erro={errors.telefone?.message}>
                  {(campo) => (
                    <Input
                      {...campo}
                      {...register('telefone', {
                        validate: validarComSeInformado(telefoneSchema),
                      })}
                      value={telefone}
                      onChange={(evento) => {
                        setValue('telefone', mascararTelefoneProgressivo(evento.target.value), {
                          shouldValidate: isSubmitted,
                        })
                      }}
                      inputMode="tel"
                      placeholder="(11) 98888-7777"
                      disabled={convidar.isPending}
                    />
                  )}
                </Campo>

                <Campo
                  rotulo="Centro de custo"
                  erro={errors.centroDeCusto?.message}
                  dica="Sobrescreve o da conta."
                >
                  {(campo) => (
                    <Input
                      {...campo}
                      {...register('centroDeCusto')}
                      disabled={convidar.isPending}
                    />
                  )}
                </Campo>
              </div>

              <Button type="submit" disabled={convidar.isPending}>
                {convidar.isPending ? (
                  <LoaderCircle className="animate-spin" aria-hidden="true" />
                ) : (
                  <UserPlus aria-hidden="true" />
                )}
                Enviar convite
              </Button>
            </form>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
